import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Result, Button } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  isChunkError: boolean;
  error?: Error;
}

/**
 * Error Boundary specifically designed to catch and handle chunk loading errors
 * that occur after deployments when lazy-loaded chunks are no longer available.
 */
class ChunkErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      isChunkError: false,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    // Check if it's a chunk loading error
    const isChunkError =
      error.message.includes('Loading chunk') ||
      error.message.includes('ChunkLoadError') ||
      error.name === 'ChunkLoadError';

    return {
      hasError: true,
      isChunkError,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ChunkErrorBoundary caught an error:', error, errorInfo);

    // If it's a chunk error, automatically reload after a short delay
    if (this.state.isChunkError) {
      // Check if we've already tried reloading (prevent infinite loops)
      const reloadCount = parseInt(sessionStorage.getItem('chunk_reload_count') || '0', 10);

      if (reloadCount < 2) {
        console.log('Chunk loading error detected. Auto-reloading in 2 seconds...');
        sessionStorage.setItem('chunk_reload_count', String(reloadCount + 1));

        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        // Max retries reached, clear counter and show manual reload button
        console.error('Chunk loading failed after multiple retries. Manual reload required.');
        sessionStorage.removeItem('chunk_reload_count');
      }
    }
  }

  handleReload = () => {
    // Clear reload counter before manual reload
    sessionStorage.removeItem('chunk_reload_count');
    window.location.reload();
  };

  componentDidMount() {
    // Clear reload counter on successful mount
    sessionStorage.removeItem('chunk_reload_count');
  }

  render() {
    if (this.state.hasError) {
      if (this.state.isChunkError) {
        return (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100vh',
              padding: '20px',
            }}
          >
            <Result
              status="warning"
              title="Application Update Available"
              subTitle="A new version of the application has been deployed. The page will reload automatically in 2 seconds..."
              extra={
                <Button type="primary" icon={<ReloadOutlined />} onClick={this.handleReload}>
                  Reload Now
                </Button>
              }
            />
          </div>
        );
      }

      // For non-chunk errors, show generic error
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            padding: '20px',
          }}
        >
          <Result
            status="error"
            title="Something went wrong"
            subTitle="An unexpected error occurred. Please try reloading the page."
            extra={
              <Button type="primary" icon={<ReloadOutlined />} onClick={this.handleReload}>
                Reload Page
              </Button>
            }
          />
        </div>
      );
    }

    return this.props.children;
  }
}

export default ChunkErrorBoundary;
